<template>
  <Card cardClasses="shadow-sm border-0">
    <!-- Header Section -->
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h4 class="m-0 text-primary">
        <i class="bi bi-receipt me-2"></i>Sales Management
      </h4>
      <div class="d-flex">
        <button class="btn btn-outline-primary me-2" @click="$router.push('/pos')">
          <i class="bi bi-cart me-1"></i> New Sale
        </button>
      </div>
    </div>

    <div class="container-fluid p-0">
      <!-- Loading Spinner -->
      <div v-if="loading" class="text-center py-5">
        <Spinner spinner-class="mb-1" />
        <p class="text-muted mt-2">Loading sales...</p>
      </div>

      <!-- Sales Content -->
      <div v-if="!loading" class="sales-content fade-in">
        <!-- Sales Count & Search -->
        <div class="d-flex flex-wrap align-items-center justify-content-between mb-3">
          <div class="d-flex align-items-center mb-2 mb-sm-0">
            <span class="badge bg-primary rounded-pill me-2">{{ sales.total }}</span>
            <span class="text-muted">Sales</span>
          </div>
          <div class="d-flex gap-2 align-items-center">
            <UInput v-model="dataTableSearch" size="md" placeholder="Search sales" class="mb-0 w-auto" />
            <button class="btn btn-primary" @click="onSearchClick">
              <i class="bi bi-search me-1"></i> Search
            </button>
            <button class="btn btn-outline-secondary" @click="onClearSearchClick" :disabled="!dataTableSearch || !dataTableSearch.length">
              <i class="bi bi-x-circle me-1"></i> Clear
            </button>
          </div>
        </div>

        <!-- Sales Table -->
        <div class="card border-0 shadow-sm mb-4">
          <div class="table-responsive">
            <table class="table table-hover align-middle mb-0">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Reference</th>
                  <th>Store</th>
                  <th>By User</th>
                  <th>Amount</th>
                  <th>Source</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(sale, index) in sales.data" :key="sale.id" class="sale-row" :class="{'bg-light': index % 2 === 0}">
                  <td>{{ sale.id.split("-").pop() }}</td>
                  <td>{{ sale.reference }}</td>
                  <td>{{ sale.store.name }}</td>
                  <td>{{ sale.user.name }}</td>
                  <td>{{ formatNumber(sale.total) }}</td>
                  <td>{{ sale.source }}</td>
                  <td>
                    <Badge pill :type="getPillByStatus(sale.status)">
                      {{ sale.status }}
                    </Badge>
                  </td>
                  <td>{{ formatDate(sale.created_at) }}</td>
                  <td>
                    <button class="btn btn-info btn-sm" @click="saleDetails(sale.id)">Details</button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Pagination Controls -->
        <div class="d-flex flex-wrap pt-3 pb-4 align-items-end mx-4 justify-content-between">
          <div class="me-sm-4 mb-sm-0 mb-3">
            <p class="mb-2 small">Rows per page</p>
            <select class="form-select" v-model.number="dataTablePerPage" data="">
              <option v-for="item in [5,10,25,50,100]" :key="item" :value="item">{{item}}</option>
            </select>
          </div>
          <div>
            <Pagination
              v-model="dataTablePage"
              :pages="totalPages"
              :threshold="2">
              <template #previous>Prev</template>
              <template #next>Next</template>
            </Pagination>
          </div>
        </div>

        <!-- Back Button -->
        <div class="d-flex justify-content-center">
          <button class="btn btn-outline-secondary" @click="$router.go(-1)">
            <i class="bi bi-arrow-left me-1"></i> Back
          </button>
        </div>
      </div>
    </div>
  </Card>
</template>

<script>
import saleService from "@/api/saleService";
import router from "@/router";
import {decrypt} from "@/utils/crypto";
import confirmDialog from "@/utils/confirmDialog";
import formatDate from "@/utils/formatDate";
import formatNumber from "@/utils/formatNumber";

export default {
  name: "Sales",

  data(){
    this.profile = JSON.parse(decrypt(sessionStorage.getItem('profile')))
    this.store = JSON.parse(decrypt(sessionStorage.getItem('store')))
    this.organization = JSON.parse(decrypt(sessionStorage.getItem('organization')))
    return {
      organization: {},
      store: {},
      profile: {},
      loading: true,
      sales: {
        data: []
      },
      dataTableSearch: this.$route?.query?.search ?? '',
      dataTablePage: this.$route?.query?.page ? parseInt(this.$route.query.page) : 1,
      dataTablePerPage: this.$route?.query?.per_page ? parseInt(this.$route.query.per_page) : 10,
      _searchDebounce: null,
      subscription: {
        package: {}
      },
    }
  },

  methods:{
    confirmDialog,
    formatDate,
    formatNumber,
    getPillByStatus(cellData) {
      switch (cellData) {
        case 'pending':
          return 'warning';
        case 'complete':
          return 'success';
        default:
          return 'secondary';
      }
    },
    async getSales() {
      this.loading = true
      const params = {
        page: this.dataTablePage,
        per_page: this.dataTablePerPage,
        search: this.dataTableSearch?.toString().trim() || undefined,
      }
      this.sales = await saleService.getAllSales(params)
      this.loading = false
    },
    saleDetails(id) {
      router.push('/sale/' + id)
    },
    onSearchClick(){
      this.dataTablePage = 1
      this.updateRouteQuery()
      this.getSales()
    },
    onClearSearchClick(){
      if (!this.dataTableSearch) return;
      this.dataTableSearch = ''
      this.dataTablePage = 1
      this.updateRouteQuery()
      this.getSales()
    },
    updateRouteQuery() {
      const query = {
        ...this.$route.query,
        page: this.dataTablePage !== 1 ? this.dataTablePage : undefined,
        per_page: this.dataTablePerPage !== 10 ? this.dataTablePerPage : undefined,
        search: this.dataTableSearch && this.dataTableSearch.trim().length ? this.dataTableSearch.trim() : undefined,
      }
      // Remove undefined keys
      Object.keys(query).forEach(k => query[k] === undefined && delete query[k])
      this.$router.replace({ path: this.$route.path, query })
    },
  },

  computed: {
    totalPages(){
      // Laravel pagination typically has last_page; fallback to 1
      return this.sales?.last_page || 1
    }
  },

  watch: {
    dataTablePerPage(){
      this.dataTablePage = 1;
      this.updateRouteQuery()
      this.getSales()
    },
    dataTablePage(){
      this.updateRouteQuery()
      this.getSales()
    },
  },

  created: async function () {
    this.store = JSON.parse(decrypt(sessionStorage.getItem('store')))
    await this.getSales()
    this.subscription = JSON.parse(decrypt(sessionStorage.getItem('subscription')))
  },
}
</script>

<style scoped>
/* Sales page styles */
.sales-content {
  padding: 0.5rem;
}

/* Table styles */
.table th {
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.7rem;
  letter-spacing: 0.5px;
  color: #6b7280;
  border-top: none;
  padding: 1rem 1.5rem;
  background-color: #f9fafb;
  border-bottom: 1px solid #e9ecef;
}

.table td {
  padding: 1rem 1.5rem;
  vertical-align: middle;
  border-color: #e9ecef;
  color: #4b5563;
}

.sale-row {
  transition: all 0.2s ease;
}

.sale-row:hover {
  background-color: #f8f9fa;
  transform: translateY(-2px);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
}

/* Badge styles */
.badge {
  font-weight: 500;
  padding: 0.35em 0.65em;
  font-size: 0.75em;
  border-radius: 6px;
}

/* Animation for content loading */
.fade-in {
  animation: fadeIn 0.3s ease-in-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
